<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import CallsAPI from 'dashboard/api/calls';
import ReportHeader from './components/ReportHeader.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const isDownloading = ref(false);
const calls = ref([]);
const metrics = ref({
  total_calls: 0,
  inbound_calls: 0,
  outbound_calls: 0,
  answered_calls: 0,
  missed_calls: 0,
  total_duration_seconds: 0,
  avg_duration_seconds: 0,
  daily_volume: {},
});

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const selectedAgentId = ref(null);
const selectedInboxId = ref(null);
const activeTab = ref('agents'); // 'agents' | 'inboxes' | 'logs'

const agents = computed(() => store.getters['agents/getAgents'] || []);
const inboxes = computed(() => {
  const all = store.getters['inboxes/getInboxes'] || [];
  return all.filter(
    i =>
      i.channel_type === 'Channel::Voice' ||
      i.channel_type === 'Channel::Telephony' ||
      i.provider === 'freeswitch' ||
      i.telephony_number ||
      (i.telephony_numbers && i.telephony_numbers.length > 0)
  );
});

const from = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const to = computed(() => getUnixEndOfDay(customDateRange.value[1]));

const formatDuration = seconds => {
  if (!seconds || seconds <= 0) return '0s';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
};

const answerRate = computed(() => {
  if (!metrics.value.total_calls || metrics.value.total_calls === 0) return 0;
  return Math.round((metrics.value.answered_calls / metrics.value.total_calls) * 100);
});

const missedRate = computed(() => {
  if (!metrics.value.total_calls || metrics.value.total_calls === 0) return 0;
  return Math.round((metrics.value.missed_calls / metrics.value.total_calls) * 100);
});

const fetchReportData = async () => {
  isLoading.value = true;
  try {
    const params = {
      since: from.value,
      until: to.value,
      agent_id: selectedAgentId.value || undefined,
      inbox_id: selectedInboxId.value || undefined,
      per_page: 500,
    };

    const [metricsRes, callsRes] = await Promise.all([
      CallsAPI.getMetrics(params).catch(() => null),
      CallsAPI.get(params).catch(() => ({ data: { payload: [] } })),
    ]);

    if (metricsRes?.data) {
      metrics.value = metricsRes.data;
    } else {
      // Fallback aggregate from calls if metrics endpoint returned null
      const payload = callsRes.data.payload || [];
      const total = payload.length;
      const inbound = payload.filter(c => c.direction === 'inbound' || c.direction === 'incoming').length;
      const outbound = total - inbound;
      const answered = payload.filter(c => c.status === 'completed' || (c.duration_seconds && c.duration_seconds > 0)).length;
      const missed = total - answered;
      const totalDur = payload.reduce((acc, c) => acc + (c.duration_seconds || 0), 0);
      const avgDur = answered > 0 ? Math.round(totalDur / answered) : 0;

      metrics.value = {
        total_calls: total,
        inbound_calls: inbound,
        outbound_calls: outbound,
        answered_calls: answered,
        missed_calls: missed,
        total_duration_seconds: totalDur,
        avg_duration_seconds: avgDur,
        daily_volume: {},
      };
    }

    calls.value = callsRes.data.payload || [];
  } catch (error) {
    useAlert(t('REPORT.DATA_FETCHING_FAILED') || 'Failed to fetch call analytics');
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChange = range => {
  customDateRange.value = range;
  fetchReportData();
};

const downloadReports = async () => {
  isDownloading.value = true;
  try {
    const params = {
      since: from.value,
      until: to.value,
      agent_id: selectedAgentId.value || undefined,
      inbox_id: selectedInboxId.value || undefined,
    };
    const response = await CallsAPI.download(params);
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `calls-report-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    useAlert('Failed to download calls report CSV');
  } finally {
    isDownloading.value = false;
  }
};

// Chart Data Preparation (Daily Call Volume Trend)
const chartData = computed(() => {
  const daysMap = {};
  const current = new Date(customDateRange.value[0]);
  const end = new Date(customDateRange.value[1]);

  while (current <= end) {
    const dStr = format(current, 'yyyy-MM-dd');
    daysMap[dStr] = {
      label: format(current, 'MMM d'),
      inbound: 0,
      outbound: 0,
    };
    current.setDate(current.getDate() + 1);
  }

  // Populate from daily_volume or individual call records
  if (metrics.value.daily_volume && Object.keys(metrics.value.daily_volume).length > 0) {
    Object.entries(metrics.value.daily_volume).forEach(([dStr, counts]) => {
      if (daysMap[dStr]) {
        daysMap[dStr].inbound = counts.inbound || 0;
        daysMap[dStr].outbound = counts.outbound || 0;
      }
    });
  } else {
    calls.value.forEach(call => {
      const dStr = format(new Date(call.created_at * 1000), 'yyyy-MM-dd');
      if (daysMap[dStr]) {
        const isOut = call.direction === 'outbound' || call.direction === 'outgoing';
        if (isOut) daysMap[dStr].outbound += 1;
        else daysMap[dStr].inbound += 1;
      }
    });
  }

  const labels = Object.values(daysMap).map(d => d.label);
  const inboundData = Object.values(daysMap).map(d => d.inbound);
  const outboundData = Object.values(daysMap).map(d => d.outbound);

  return {
    labels,
    datasets: [
      {
        label: 'Inbound Calls',
        backgroundColor: '#0ea5e9', // Sky blue
        hoverBackgroundColor: '#0284c7',
        borderRadius: 4,
        data: inboundData,
      },
      {
        label: 'Outbound Calls',
        backgroundColor: '#10b981', // Emerald green
        hoverBackgroundColor: '#059669',
        borderRadius: 4,
        data: outboundData,
      },
    ],
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top',
      labels: {
        boxWidth: 12,
        usePointStyle: true,
        font: { family: 'Inter,-apple-system,system-ui,sans-serif', size: 12 },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 11 } },
    },
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(150, 150, 150, 0.1)' },
      ticks: { stepSize: 1, precision: 0, font: { size: 11 } },
    },
  },
};

// Agent Performance Breakdown
const agentBreakdown = computed(() => {
  const map = {};
  calls.value.forEach(call => {
    const agentId = call.agent?.id || 'unassigned';
    const agentName = call.agent?.name || 'Unassigned / System';
    const avatar = call.agent?.avatar || '';

    if (!map[agentId]) {
      map[agentId] = {
        id: agentId,
        name: agentName,
        avatar,
        total: 0,
        inbound: 0,
        outbound: 0,
        answered: 0,
        missed: 0,
        duration: 0,
      };
    }

    map[agentId].total += 1;
    const isOut = call.direction === 'outbound' || call.direction === 'outgoing';
    if (isOut) map[agentId].outbound += 1;
    else map[agentId].inbound += 1;

    const isAnswered = call.status === 'completed' || (call.duration_seconds && call.duration_seconds > 0);
    if (isAnswered) {
      map[agentId].answered += 1;
      map[agentId].duration += call.duration_seconds || 0;
    } else {
      map[agentId].missed += 1;
    }
  });

  return Object.values(map).sort((a, b) => b.total - a.total);
});

// Voice Inbox / Line Breakdown
const inboxBreakdown = computed(() => {
  const map = {};
  calls.value.forEach(call => {
    const inboxId = call.inbox?.id || 'default';
    const inboxName = call.inbox?.name || 'Voice Trunk';
    const channelType = call.inbox?.channel_type || 'Channel::Voice';

    if (!map[inboxId]) {
      map[inboxId] = {
        id: inboxId,
        name: inboxName,
        channelType,
        total: 0,
        inbound: 0,
        outbound: 0,
        duration: 0,
      };
    }

    map[inboxId].total += 1;
    const isOut = call.direction === 'outbound' || call.direction === 'outgoing';
    if (isOut) map[inboxId].outbound += 1;
    else map[inboxId].inbound += 1;

    map[inboxId].duration += call.duration_seconds || 0;
  });

  return Object.values(map).sort((a, b) => b.total - a.total);
});

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  fetchReportData();
});
</script>

<template>
  <div class="flex flex-col gap-6 pb-12">
    <!-- Header -->
    <ReportHeader
      header-title="Calls"
      header-description="Metrics, performance, and volume analytics for inbound, outbound, and telephony voice calls."
    >
      <V4Button
        label="Download Report"
        icon="i-ph-download-simple"
        size="sm"
        :is-loading="isDownloading"
        @click="downloadReports"
      />
    </ReportHeader>

    <!-- Filter Bar -->
    <div class="flex flex-wrap items-center justify-between gap-4 p-4 rounded-2xl border bg-n-solid-1/60 dark:bg-n-solid-1 border-n-weak">
      <div class="flex flex-wrap items-center gap-3">
        <!-- Date Picker -->
        <WootDatePicker
          :value="customDateRange"
          :selected-range="selectedDateRange"
          @change="onDateRangeChange"
        />

        <!-- Agent Filter -->
        <select
          v-model="selectedAgentId"
          class="h-9 px-3 text-xs font-medium rounded-xl border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
          @change="fetchReportData"
        >
          <option :value="null">All Agents</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>

        <!-- Inbox Filter -->
        <select
          v-model="selectedInboxId"
          class="h-9 px-3 text-xs font-medium rounded-xl border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
          @change="fetchReportData"
        >
          <option :value="null">All Voice Lines</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <V4Button
        icon="i-lucide-refresh-cw"
        ghost
        slate
        size="sm"
        :is-loading="isLoading"
        @click="fetchReportData"
      />
    </div>

    <!-- KPI Metric Summary Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- Total Calls -->
      <div class="p-5 rounded-2xl border bg-n-surface-1 border-n-weak flex flex-col gap-1.5 shadow-none">
        <div class="flex items-center justify-between text-n-slate-11 text-xs font-medium">
          <span>Total Calls</span>
          <div class="p-1.5 rounded-lg bg-n-teal-3 text-n-teal-11">
            <Icon icon="i-lucide-phone" class="size-4" />
          </div>
        </div>
        <div class="text-2xl font-bold text-n-slate-12 mt-1">
          {{ metrics.total_calls || 0 }}
        </div>
        <div class="flex items-center gap-2 text-xs text-n-slate-10">
          <span>{{ metrics.inbound_calls || 0 }} in</span>
          <span>•</span>
          <span>{{ metrics.outbound_calls || 0 }} out</span>
        </div>
      </div>

      <!-- Answered Calls -->
      <div class="p-5 rounded-2xl border bg-n-surface-1 border-n-weak flex flex-col gap-1.5 shadow-none">
        <div class="flex items-center justify-between text-n-slate-11 text-xs font-medium">
          <span>Answered Calls</span>
          <div class="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <Icon icon="i-lucide-phone-incoming" class="size-4" />
          </div>
        </div>
        <div class="text-2xl font-bold text-n-slate-12 mt-1 flex items-baseline gap-2">
          <span>{{ metrics.answered_calls || 0 }}</span>
          <span class="text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-md">
            {{ answerRate }}% rate
          </span>
        </div>
        <div class="text-xs text-n-slate-10">
          Successfully connected
        </div>
      </div>

      <!-- Missed Calls -->
      <div class="p-5 rounded-2xl border bg-n-surface-1 border-n-weak flex flex-col gap-1.5 shadow-none">
        <div class="flex items-center justify-between text-n-slate-11 text-xs font-medium">
          <span>Missed / Unanswered</span>
          <div class="p-1.5 rounded-lg bg-ruby-500/10 text-ruby-9 dark:text-ruby-8">
            <Icon icon="i-lucide-phone-missed" class="size-4" />
          </div>
        </div>
        <div class="text-2xl font-bold text-n-slate-12 mt-1 flex items-baseline gap-2">
          <span>{{ metrics.missed_calls || 0 }}</span>
          <span class="text-xs font-medium text-ruby-9 dark:text-ruby-8 bg-ruby-500/10 px-1.5 py-0.5 rounded-md">
            {{ missedRate }}% rate
          </span>
        </div>
        <div class="text-xs text-n-slate-10">
          Unanswered or dropped
        </div>
      </div>

      <!-- Total Talk Time -->
      <div class="p-5 rounded-2xl border bg-n-surface-1 border-n-weak flex flex-col gap-1.5 shadow-none">
        <div class="flex items-center justify-between text-n-slate-11 text-xs font-medium">
          <span>Total Talk Time</span>
          <div class="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
            <Icon icon="i-lucide-timer" class="size-4" />
          </div>
        </div>
        <div class="text-2xl font-bold text-n-slate-12 mt-1">
          {{ formatDuration(metrics.total_duration_seconds) }}
        </div>
        <div class="text-xs text-n-slate-10">
          Avg: {{ formatDuration(metrics.avg_duration_seconds) }} / call
        </div>
      </div>
    </div>

    <!-- Chart: Call Volume Trend -->
    <div class="p-6 rounded-2xl border bg-n-surface-1 border-n-weak flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12 m-0">Call Volume Trend</h3>
          <p class="text-xs text-n-slate-10 mt-0.5 mb-0">Daily breakdown of inbound vs outbound calls</p>
        </div>
      </div>

      <div class="h-64 w-full relative">
        <Spinner v-if="isLoading" class="absolute inset-0 m-auto" />
        <BarChart
          v-else
          :collection="chartData"
          :chart-options="chartOptions"
        />
      </div>
    </div>

    <!-- Performance Breakdown Section -->
    <div class="flex flex-col gap-4">
      <!-- Tabs Switcher -->
      <div class="flex items-center gap-2 border-b border-n-weak pb-2">
        <button
          class="px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
          :class="activeTab === 'agents' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'"
          @click="activeTab = 'agents'"
        >
          By Agent ({{ agentBreakdown.length }})
        </button>
        <button
          class="px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
          :class="activeTab === 'inboxes' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'"
          @click="activeTab = 'inboxes'"
        >
          By Voice Line ({{ inboxBreakdown.length }})
        </button>
        <button
          class="px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
          :class="activeTab === 'logs' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'"
          @click="activeTab = 'logs'"
        >
          Recent Calls ({{ calls.length }})
        </button>
      </div>

      <!-- Agent Breakdown Table -->
      <div v-if="activeTab === 'agents'" class="overflow-x-auto rounded-2xl border border-n-weak bg-n-surface-1">
        <table class="w-full text-left text-xs text-n-slate-11">
          <thead class="bg-n-solid-1/70 border-b border-n-weak text-n-slate-12 font-semibold">
            <tr>
              <th class="p-3.5 pl-4">Agent</th>
              <th class="p-3.5">Total Calls</th>
              <th class="p-3.5">Inbound</th>
              <th class="p-3.5">Outbound</th>
              <th class="p-3.5">Answered</th>
              <th class="p-3.5">Missed</th>
              <th class="p-3.5 pr-4">Total Talk Time</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-if="agentBreakdown.length === 0">
              <td colspan="7" class="p-6 text-center text-n-slate-10">No calls recorded in this date range.</td>
            </tr>
            <tr v-for="agent in agentBreakdown" :key="agent.id" class="hover:bg-n-alpha-1/50 transition-colors">
              <td class="p-3.5 pl-4 flex items-center gap-2.5 font-medium text-n-slate-12">
                <Avatar :name="agent.name" :src="agent.avatar" size="24px" class="shrink-0" />
                <span class="truncate">{{ agent.name }}</span>
              </td>
              <td class="p-3.5 font-semibold text-n-slate-12">{{ agent.total }}</td>
              <td class="p-3.5 text-sky-600 dark:text-sky-400">{{ agent.inbound }}</td>
              <td class="p-3.5 text-emerald-600 dark:text-emerald-400">{{ agent.outbound }}</td>
              <td class="p-3.5 text-emerald-600 dark:text-emerald-400">{{ agent.answered }}</td>
              <td class="p-3.5 text-ruby-9 dark:text-ruby-8">{{ agent.missed }}</td>
              <td class="p-3.5 pr-4 font-mono text-n-slate-12">{{ formatDuration(agent.duration) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Inboxes Breakdown Table -->
      <div v-if="activeTab === 'inboxes'" class="overflow-x-auto rounded-2xl border border-n-weak bg-n-surface-1">
        <table class="w-full text-left text-xs text-n-slate-11">
          <thead class="bg-n-solid-1/70 border-b border-n-weak text-n-slate-12 font-semibold">
            <tr>
              <th class="p-3.5 pl-4">Voice Line / Inbox</th>
              <th class="p-3.5">Total Calls</th>
              <th class="p-3.5">Inbound</th>
              <th class="p-3.5">Outbound</th>
              <th class="p-3.5 pr-4">Total Talk Time</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-if="inboxBreakdown.length === 0">
              <td colspan="5" class="p-6 text-center text-n-slate-10">No voice line records found.</td>
            </tr>
            <tr v-for="inbox in inboxBreakdown" :key="inbox.id" class="hover:bg-n-alpha-1/50 transition-colors">
              <td class="p-3.5 pl-4 font-medium text-n-slate-12 flex items-center gap-2">
                <div class="p-1 rounded bg-n-teal-3 text-n-teal-11">
                  <Icon icon="i-lucide-phone" class="size-3.5" />
                </div>
                <span>{{ inbox.name }}</span>
              </td>
              <td class="p-3.5 font-semibold text-n-slate-12">{{ inbox.total }}</td>
              <td class="p-3.5 text-sky-600 dark:text-sky-400">{{ inbox.inbound }}</td>
              <td class="p-3.5 text-emerald-600 dark:text-emerald-400">{{ inbox.outbound }}</td>
              <td class="p-3.5 pr-4 font-mono text-n-slate-12">{{ formatDuration(inbox.duration) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Recent Calls Log Preview Table -->
      <div v-if="activeTab === 'logs'" class="overflow-x-auto rounded-2xl border border-n-weak bg-n-surface-1">
        <table class="w-full text-left text-xs text-n-slate-11">
          <thead class="bg-n-solid-1/70 border-b border-n-weak text-n-slate-12 font-semibold">
            <tr>
              <th class="p-3.5 pl-4">Type</th>
              <th class="p-3.5">Contact</th>
              <th class="p-3.5">Status</th>
              <th class="p-3.5">Duration</th>
              <th class="p-3.5">Agent</th>
              <th class="p-3.5">Voice Line</th>
              <th class="p-3.5 pr-4">Time</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-if="calls.length === 0">
              <td colspan="7" class="p-6 text-center text-n-slate-10">No calls found in this date range.</td>
            </tr>
            <tr v-for="call in calls" :key="call.id" class="hover:bg-n-alpha-1/50 transition-colors">
              <td class="p-3.5 pl-4">
                <div class="flex items-center gap-1.5 font-medium" :class="(call.direction === 'outbound' || call.direction === 'outgoing') ? 'text-emerald-600 dark:text-emerald-400' : 'text-sky-600 dark:text-sky-400'">
                  <Icon :icon="(call.direction === 'outbound' || call.direction === 'outgoing') ? 'i-lucide-phone-outgoing' : 'i-lucide-phone-incoming'" class="size-3.5" />
                  <span class="capitalize">{{ call.direction || 'Inbound' }}</span>
                </div>
              </td>
              <td class="p-3.5 text-n-slate-12 font-medium">
                <div>{{ call.contact?.name || 'Unknown' }}</div>
                <div class="text-[11px] text-n-slate-10 font-mono">{{ call.contact?.phone_number || '' }}</div>
              </td>
              <td class="p-3.5">
                <span
                  class="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase"
                  :class="call.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-ruby-500/10 text-ruby-9 dark:text-ruby-8'"
                >
                  {{ call.status || 'ended' }}
                </span>
              </td>
              <td class="p-3.5 font-mono text-n-slate-12">
                {{ formatDuration(call.duration_seconds) }}
              </td>
              <td class="p-3.5 text-n-slate-12">
                {{ call.agent?.name || '—' }}
              </td>
              <td class="p-3.5 text-n-slate-11">
                {{ call.inbox?.name || 'Voice Trunk' }}
              </td>
              <td class="p-3.5 pr-4 text-n-slate-10 whitespace-nowrap">
                {{ format(new Date(call.created_at * 1000), 'MMM d, h:mm a') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
